import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import sagradaFamilia from '../image/location/sagradafamilia.webp';
import parkGuell from '../image/location/parkguell.jpg';
import casaBatllo from '../image/location/casabatllo.jpg';
import gothicQuarter from '../image/location/gothicquater.webp';
import barceloneta from '../image/location/barceloneta.jpg';
import montjuic from '../image/location/montjuic.jpg';
import parcCiutadella from '../image/location/parcciutadella.jpg';
import tibidabo from '../image/location/tibidabo.jpg';
import palauMusica from '../image/location/palaumusicacatalana.jpg';

const ExploreBarcelona = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const attractions = [
        {
            id: 1,
            name: t('explore.attractions.1.name'),
            image: sagradaFamilia,
            description: t('explore.attractions.1.description'),
            color: "from-amber-500 to-orange-600",
            textColor: "text-amber-900",
            url: "https://sagradafamilia.org/"
        },
        {
            id: 2,
            name: t('explore.attractions.2.name'),
            image: parkGuell,
            description: t('explore.attractions.2.description'),
            color: "from-green-500 to-emerald-600",
            textColor: "text-green-900",
            url: "https://parkguell.barcelona/"
        },
        {
            id: 3,
            name: t('explore.attractions.3.name'),
            image: casaBatllo,
            description: t('explore.attractions.3.description'),
            color: "from-blue-500 to-indigo-600",
            textColor: "text-blue-900",
            url: "https://www.casabatllo.es/"
        },
        {
            id: 4,
            name: t('explore.attractions.4.name'),
            image: gothicQuarter,
            description: t('explore.attractions.4.description'),
            color: "from-purple-500 to-pink-600",
            textColor: "text-purple-900",
            url: "https://www.barrigotic.cat/"
        },
        {
            id: 5,
            name: t('explore.attractions.5.name'),
            image: barceloneta,
            description: t('explore.attractions.5.description'),
            color: "from-cyan-500 to-blue-600",
            textColor: "text-cyan-900",
            url: "https://www.barcelona.cat/en/what-to-do-in-bcn/bathing-and-beaches/barceloneta-beach"
        },
        {
            id: 6,
            name: t('explore.attractions.6.name'),
            image: montjuic,
            description: t('explore.attractions.6.description'),
            color: "from-rose-500 to-red-600",
            textColor: "text-rose-900",
            url: "https://www.barcelona.cat/es/que-hacer-en-bcn/parque-montjuic"
        },
        {
            id: 7,
            name: t('explore.attractions.7.name'),
            image: parcCiutadella,
            description: t('explore.attractions.7.description'),
            color: "from-teal-500 to-cyan-600",
            textColor: "text-teal-900",
            url: "https://www.barcelona.cat/es/que-hacer-en-bcn/parques-y-jardines/parque-de-la-ciutadella-92086011921"
        },
        {
            id: 8,
            name: t('explore.attractions.8.name'),
            image: tibidabo,
            description: t('explore.attractions.8.description'),
            color: "from-sky-500 to-blue-600",
            textColor: "text-sky-900",
            url: "https://tibidabo.cat/"
        },
        {
            id: 9,
            name: t('explore.attractions.9.name'),
            image: palauMusica,
            description: t('explore.attractions.9.description'),
            color: "from-fuchsia-500 to-pink-600",
            textColor: "text-fuchsia-900",
            url: "https://www.palaumusica.cat/ca"
        }
    ];

    return (
        <div className="min-h-screen bg-white font-montserrat pt-24">
            {/* Hero Section */}
            <div className="relative overflow-hidden bg-slate-900 py-32 animate-fade-in">
                <div className="absolute inset-0 z-0">
                    <img
                        src={barceloneta}
                        alt="Barcelona Background"
                        className="w-full h-full object-cover opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-white"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-3 px-5 py-2 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-black uppercase tracking-[0.2em] shadow-2xl mb-8 animate-fade-in-up">
                        <span>City Discovery</span>
                    </div>
                    <h1 className="text-5xl md:text-8xl font-black text-white leading-[0.9] tracking-tighter drop-shadow-2xl mb-8 animate-fade-in-up">
                        {t('explore.hero.title')}
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-lg animate-fade-in-up stagger-1">
                        {t('explore.hero.subtitle')}
                    </p>
                </div>
            </div>

            {/* Attractions Grid */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                    {attractions.map((attraction, index) => (
                        <div
                            key={attraction.id}
                            className={`group relative bg-white rounded-[32px] shadow-[0_20px_40px_-10px_rgba(0,0,0,0.06)] overflow-hidden transform transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.12)] border border-slate-100 animate-fade-in-up stagger-${index + 1}`}
                        >
                            {/* Image Container */}
                            <div className="relative h-72 overflow-hidden">
                                <img
                                    src={attraction.image}
                                    alt={attraction.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                {/* Gradient Overlay */}
                                <div className={`absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500`}></div>

                                {/* Number Badge */}
                                <div className={`absolute top-6 right-6 w-12 h-12 bg-white/90 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-xl border border-white/50`}>
                                    <span className="text-slate-900 font-black text-lg">{attraction.id}</span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-8">
                                <h3 className={`text-2xl font-black mb-4 text-slate-900 uppercase tracking-tight flex items-center gap-2`}>
                                    {attraction.name}
                                </h3>
                                <p className="text-slate-500 font-medium leading-relaxed mb-8 h-24 overflow-hidden overflow-ellipsis line-clamp-3">
                                    {attraction.description}
                                </p>

                                {/* Action Button */}
                                <a
                                    href={attraction.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`inline-flex items-center gap-2 py-4 px-8 bg-slate-900 text-white font-black uppercase text-xs tracking-widest rounded-2xl shadow-lg hover:bg-blue-600 transition-all duration-300 w-full justify-center group/btn`}
                                >
                                    Learn More
                                    <span className="transition-transform group-hover/btn:translate-x-1">→</span>
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Call to Action Section */}
            <div className="bg-slate-50 py-32 animate-fade-in border-t border-slate-100">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-4xl md:text-6xl font-black text-slate-900 uppercase tracking-tighter mb-6">
                        {t('explore.cta.title')}
                    </h2>
                    <p className="text-xl md:text-2xl text-slate-500 font-medium leading-relaxed mb-12">
                        {t('explore.cta.subtitle')}
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-12 py-6 bg-blue-600 text-white font-black uppercase text-sm tracking-[0.2em] rounded-2xl shadow-[0_20px_50px_rgba(37,99,235,0.4)] hover:bg-blue-700 hover:scale-105 active:scale-95 transition-all duration-300"
                    >
                        {t('explore.cta.button')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExploreBarcelona;
